import { useTranslation } from 'react-i18next';
import { FieldShell } from '@/lib/ui/Field';
import { Button } from '@/lib/ui/Button';
import { MonoValue } from '@/lib/ui/Typography';
import { usePicker } from '../PickerContext';

/** format:uuid image field → opens the Media Library picker modal (spec §9.1 / §10). */
export function MediaPickerField({
  label,
  value,
  onChange,
  required,
  error,
  disabled,
}: {
  label: string;
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { pickMedia, MediaPreview } = usePicker();
  return (
    <FieldShell label={t(label, { defaultValue: label })} required={required} error={error}>
      <div className="flex items-center gap-md rounded-sm border-[0.5px] border-hairline bg-canvas-sand p-sm">
        <MediaPreview id={value ?? null} />
        <div className="flex flex-1 flex-col gap-[2px]">
          {value ? (
            <MonoValue className="text-small text-ink-mid">{value}</MonoValue>
          ) : (
            <span className="text-small text-ink-mute">{t('forms.noMediaSelected')}</span>
          )}
        </div>
        <div className="flex gap-xs">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={async () => {
              const id = await pickMedia();
              if (id) onChange(id);
            }}
          >
            {t('forms.choose')}
          </Button>
          {value && !disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
              {t('common.clear')}
            </Button>
          )}
        </div>
      </div>
    </FieldShell>
  );
}

/** format:uuid + x-entity → opens a collection picker for content references. */
export function EntityPickerField({
  label,
  collection,
  value,
  onChange,
  required,
  error,
  disabled,
}: {
  label: string;
  collection: string;
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { pickEntity } = usePicker();
  return (
    <FieldShell label={t(label, { defaultValue: label })} required={required} error={error}>
      <div className="flex items-center gap-md rounded-sm border-[0.5px] border-hairline bg-canvas-sand p-sm">
        <span className="flex-1 text-small">
          {value ? (
            <MonoValue className="text-ink-mid">{value}</MonoValue>
          ) : (
            <span className="text-ink-mute">{t('forms.noEntitySelected', { collection })}</span>
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={async () => {
            const id = await pickEntity(collection);
            if (id) onChange(id);
          }}
        >
          {t('forms.choose')}
        </Button>
      </div>
    </FieldShell>
  );
}
